import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DevExtremeModule } from 'devextreme-angular';
import moment from 'moment';
import { AppInformationService, CdrUnitOfWork, SessionInformationService, UtilityService } from 'src/app/core/core.module';
import { DialogService } from 'src/app/shared/dialog.service';
import { CustomerInvoicesDialogComponent } from '../customer-invoices-dialog/customer-invoices-dialog.component';
import { CustomerRentalsDialogComponent } from '../customer-rentals-dialog/customer-rentals-dialog.component';
import { CustomerJobsDialogComponent } from '../customer-jobs-dialog/customer-jobs-dialog.component';

@Component({
    selector: 'app-customer-totals',
    standalone: true,
    templateUrl: './customer-totals.component.html',
    styleUrls: ['./customer-totals.component.scss'],
    imports: [CommonModule, RouterModule, DevExtremeModule]
})
export class CustomerTotalsComponent {
    uow = inject(CdrUnitOfWork);
    appInfo = inject(AppInformationService);
    sessionInfo = inject(SessionInformationService);
    utility = inject(UtilityService);
    dialogService = inject(DialogService);

    filter = { startDate: new Date(), endDate: new Date() };
    showPreviousWeek: boolean;
    feeRevenue: { id: number; name: string; first: Date; last: Date; count: number; total: number }[];
    customerTotals: any;
    customerRentalMarginAvgs: any;

    async ngOnInit() {
        const filter = this.sessionInfo.getItem('revenue-forecast-filter');
        if (filter) this.filter = filter;
        else this.setFilter();
        await this.getData();
    }

    async getData() {
        this.customerRentalMarginAvgs = await this.uow.customers.getCustomerRentalMarginAvgs();
        if (!this.filter.startDate || !this.filter.endDate) {
            this.feeRevenue = null;
            this.customerTotals = null;
            return;
        }

        this.appInfo.pleaseWaitShow();
        this.feeRevenue = await this.uow.jobs.getFeeRevenue(this.filter.startDate, this.filter.endDate);
        this.customerTotals = await this.uow.customers.getCustomerTotals(this.filter.startDate, this.filter.endDate);
        this.customerTotals.forEach(
            x => (x.allTimeAvgMargin = this.customerRentalMarginAvgs.filter(y => y.id == x.id)[0].allTimeAvgMargin)
        );
        this.sessionInfo.setItem('revenue-forecast-filter', this.filter);
        this.appInfo.pleaseWaitHide();
    }

    async toggleShowPreviousWeek() {
        this.showPreviousWeek = !this.showPreviousWeek;
        this.setFilter();
        await this.getData();
    }

    setFilter() {
        const start = new Date();
        // 0 = Sun, 1 = Mon, etc.
        const day = start.getDay();
        // -6 if day is Sun; otherwise +1
        let daysPastMonday = start.getDate() - day + (day === 0 ? -6 : 1);
        if (this.showPreviousWeek) daysPastMonday -= 7;
        start.setDate(daysPastMonday);
        const end = moment(start).add(6, 'days').toDate();

        this.filter.startDate = start;
        this.filter.endDate = end;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons'
            },
            {
                template: 'toolbarText',
                location: 'after'
            },
            {
                location: 'after',
                widget: 'dxButton',
                locateInMenu: 'always',
                options: {
                    icon: 'refresh',
                    text: 'Reset Filters',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    }
                }
            }
        );
    }

    async viewInvoices(customerId: number) {
        const dialogRef = this.dialogService.open(CustomerInvoicesDialogComponent);
        dialogRef.instance.customerId = customerId;
        dialogRef.instance.startDate = this.filter.startDate;
        dialogRef.instance.endDate = this.filter.endDate;
        await dialogRef.instance.show();
        this.dialogService.close(dialogRef);
    }

    async viewRentals(customerId: number) {
        const dialogRef = this.dialogService.open(CustomerRentalsDialogComponent);
        dialogRef.instance.customerId = customerId;
        dialogRef.instance.startDate = this.filter.startDate;
        dialogRef.instance.endDate = this.filter.endDate;
        await dialogRef.instance.show();
        this.dialogService.close(dialogRef);
        await this.getData();
    }

    async viewJobs(customerId: number) {
        const dialogRef = this.dialogService.open(CustomerJobsDialogComponent);
        dialogRef.instance.customerId = customerId;
        dialogRef.instance.startDate = this.filter.startDate;
        dialogRef.instance.endDate = this.filter.endDate;
        await dialogRef.instance.show();
        this.dialogService.close(dialogRef);
    }

    async recalcMargins(customers: any[]) {
        this.appInfo.pleaseWaitShow();
        for (const customer of customers) {
            await this.uow.rentals.getRentalsByCustomer(customer.id, this.filter.startDate, this.filter.endDate, true);
        }
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }
}
